import { createSlice } from "@reduxjs/toolkit";
import {
    fetchJokerSettings,
    saveJokerSettings,
    fetchJokerSession,
    fetchJokerSessionDetails
} from "./thunk";

const initialState = {
    loading: false,
    updating: false,
    error: null,

    jokerEnabled: false,
    jokerNumber: null,
    jokerStartTime: null,
    jokerEndTime: null,
    jokerMultiplier: 1,
    jokerId: null, // Store joker ID for edit mode

    // Active/Latest Session Data
    currentSession: null,
    
    // Detailed Session Data (with rounds, winners, transactions)
    sessionDetails: null,
    detailsLoading: false,

    successMessage: ""
};

const jokerSettingsSlice = createSlice({
    name: "jokerSettings",
    initialState,
    reducers: {
        clearJokerSettingsMessage(state) {
            state.successMessage = "";
            state.error = null;
        },
        setEditMode(state, action) {
            // Set form data for edit mode
            const jokerData = action.payload;
            if (jokerData) {
                state.jokerId = jokerData.id || jokerData._id || null;
                state.jokerNumber = jokerData.totalJokerRounds || jokerData.jokerNumber || null;
                state.jokerStartTime = jokerData.startTime || null;
                state.jokerEndTime = jokerData.endTime || null;
                state.jokerMultiplier = jokerData.multiplier || 2;
            }
        },
        clearEditMode(state) {
            state.jokerId = null;
            state.jokerNumber = null;
            state.jokerStartTime = null;
            state.jokerEndTime = null;
            state.jokerMultiplier = 2;
        }
    },
    extraReducers: (builder) => {
        builder

            /* ================= FETCH SETTINGS ================= */
            .addCase(fetchJokerSettings.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchJokerSettings.fulfilled, (state, action) => {
                state.loading = false;

                // Thunk returns the mapped object directly now
                const data = action.payload || {};

                state.jokerEnabled = data.jokerEnabled;
                state.jokerNumber = data.totalJokerRounds;
                state.jokerStartTime = data.startTime;
                state.jokerEndTime = data.endTime;
                state.jokerMultiplier = data.multiplier;
                state.jokerId = data.jokerId || null;

                // Sync current session if available (can be running or pending)
                if (data.currentSession) {
                    state.currentSession = data.currentSession;
                    // Store joker ID from session if available
                    if (data.currentSession.id || data.currentSession._id) {
                        state.jokerId = data.currentSession.id || data.currentSession._id;
                    }
                } else {
                    // Clear current session if no session exists
                    state.currentSession = null;
                }
            })
            .addCase(fetchJokerSettings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            /* ================= FETCH JOKER SESSION ================= */
            .addCase(fetchJokerSession.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchJokerSession.fulfilled, (state, action) => {
                state.loading = false;
                const session = action.payload?.data || action.payload || null;
                state.currentSession = session;
                
                // Store joker ID from session if available
                if (session && (session.id || session._id)) {
                    state.jokerId = session.id || session._id;
                    // Also sync other fields if available
                    if (session.totalJokerRounds) state.jokerNumber = session.totalJokerRounds;
                    if (session.startTime) state.jokerStartTime = session.startTime;
                    if (session.endTime) state.jokerEndTime = session.endTime;
                    if (session.multiplier) state.jokerMultiplier = session.multiplier;
                }
            })
            .addCase(fetchJokerSession.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            /* ================= UPDATE SETTINGS ================= */
            .addCase(saveJokerSettings.pending, (state) => {
                state.updating = true;
                state.error = null;
                state.successMessage = "";
            })
            .addCase(saveJokerSettings.fulfilled, (state, action) => {
                state.updating = false;

                const data = action.payload?.data || action.payload || {};

                state.jokerEnabled = true;
                state.jokerNumber = data.jokerNumber || data.totalJokerRounds;
                state.jokerStartTime = data.startTime || data.jokerStartTime;
                state.jokerEndTime = data.endTime || data.jokerEndTime;
                state.jokerMultiplier = data.multiplier || data.jokerMultiplier;
                state.jokerId = action.payload?.jokerId || data.id || state.jokerId;

                state.successMessage = action.payload?.message || "Joker saved successfully";
            })
            .addCase(saveJokerSettings.rejected, (state, action) => {
                state.updating = false;
                state.error = action.payload;
            })

            /* ================= FETCH JOKER SESSION DETAILS ================= */
            .addCase(fetchJokerSessionDetails.pending, (state) => {
                state.detailsLoading = true;
                state.error = null;
            })
            .addCase(fetchJokerSessionDetails.fulfilled, (state, action) => {
                state.detailsLoading = false;
                // The thunk already extracts res.data, so action.payload is the session data directly
                state.sessionDetails = action.payload || null;
            })
            .addCase(fetchJokerSessionDetails.rejected, (state, action) => {
                state.detailsLoading = false;
                state.error = action.payload;
            });
    }
});

export const { clearJokerSettingsMessage, setEditMode, clearEditMode } = jokerSettingsSlice.actions;
export default jokerSettingsSlice.reducer;
